import { identityKey, type LevelIdentity } from './model/identity';
import type { GeneratedLevel } from './model/level';
import type { Repository } from './persistence/repository';
import { GenerationClient } from './workers/generationClient';

/**
 * Level acquisition: memory cache -> IndexedDB cache -> worker generation.
 *
 * Cached records are validated (identity and board hash) before use, so a level cached by
 * an older build is discarded rather than played. Requests for the same identity share one
 * in-flight promise, which also makes rapid navigation cheap.
 */
export class LevelService {
  private readonly memory = new Map<string, GeneratedLevel>();
  private readonly inFlight = new Map<string, Promise<GeneratedLevel>>();
  private readonly client: GenerationClient;
  private readonly repository: Repository | null;

  constructor(repository: Repository | null, client = new GenerationClient()) {
    this.repository = repository;
    this.client = client;
  }

  get usingWorker(): boolean {
    return this.client.usingWorker;
  }

  peek(identity: LevelIdentity): GeneratedLevel | undefined {
    return this.memory.get(identityKey(identity));
  }

  async getLevel(identity: LevelIdentity): Promise<GeneratedLevel> {
    const key = identityKey(identity);

    const cached = this.memory.get(key);
    if (cached !== undefined) return cached;

    const existing = this.inFlight.get(key);
    if (existing !== undefined) return existing;

    const request = this.load(identity, key).finally(() => {
      this.inFlight.delete(key);
    });
    this.inFlight.set(key, request);
    return request;
  }

  private async load(identity: LevelIdentity, key: string): Promise<GeneratedLevel> {
    if (this.repository !== null) {
      const stored = await this.repository.loadCachedLevel(identity);
      if (stored !== null) {
        this.memory.set(key, stored);
        return stored;
      }
    }

    const level = await this.client.generate(identity);
    this.memory.set(key, level);
    if (this.repository !== null) {
      // Caching is an optimisation; a storage failure must not fail the level.
      void this.repository.cacheLevel(level).catch(() => undefined);
    }
    return level;
  }

  /** Warms the cache for the next level while the player is busy with the current one. */
  prefetch(identity: LevelIdentity): void {
    void this.getLevel(identity).catch(() => undefined);
  }

  dispose(): void {
    this.client.dispose();
    this.memory.clear();
    this.inFlight.clear();
  }
}
